import React, { useEffect, useMemo, useRef, useState } from "react";
import { useRecordContext } from "react-admin";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
  Chip,
  LinearProgress,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import purify from "dompurify";

const PREVIEW_BUTTON_SX = {
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 700,
  px: 1.25,
  color: "#0f172a",
  background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
  border: "1px solid rgba(15,23,42,0.12)",
  boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
  "&:hover": {
    background: "linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%)",
    boxShadow: "0 4px 12px rgba(15, 23, 42, 0.12)",
  },
};

const VIDEO_PREVIEW_MESSAGE =
  "Pré-visualização limitada. Faça o download para acessar a mídia completa.";

const AUDIO_PREVIEW_MESSAGE =
  "Pré-visualização disponível para reprodução antes do download.";

const TEXT_PREVIEW_MESSAGE =
  "Pré-visualização de texto. Faça o download para acessar a versão final completa.";

const CustomerMediaPreview = () => {
  const record = useRecordContext();
  const [open, setOpen] = useState(false);
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioCurrentTime, setAudioCurrentTime] = useState(0);

  const isVideo = record && record.media_class === "Video" && record.url;
  const isAudio = record && record.media_class === "Audio" && record.url;
  const isText = record && record.media_class === "Texto" && record.body;

  const hasPreview = Boolean(isVideo || isAudio || isText);

  const stopPropagation = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleOpen = (event) => {
    stopPropagation(event);
    setOpen(true);
  };

  const handleClose = (event) => {
    if (event) stopPropagation(event);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setAudioPlaying(false);
    setAudioCurrentTime(0);
    setOpen(false);
  };

  const textBody = useMemo(() => {
    if (!isText) return "";
    return purify.sanitize(record.body || "");
  }, [isText, record]);

  useEffect(() => {
    if (!open || !isAudio || !record || !record.url) return undefined;

    const audio = new Audio(record.url);
    audioRef.current = audio;

    const onLoadedMetadata = () => {
      setAudioDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
    };
    const onTimeUpdate = () => {
      setAudioCurrentTime(audio.currentTime || 0);
    };
    const onEnded = () => {
      setAudioPlaying(false);
      setAudioCurrentTime(0);
    };

    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("ended", onEnded);

    return () => {
      audio.pause();
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("ended", onEnded);
      audioRef.current = null;
      setAudioPlaying(false);
      setAudioCurrentTime(0);
      setAudioDuration(0);
    };
  }, [open, isAudio, record]);

  const formatTime = (value) => {
    if (!Number.isFinite(value) || value < 0) return "00:00";
    const mins = Math.floor(value / 60);
    const secs = Math.floor(value % 60);
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const toggleAudioPlay = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;

    try {
      if (audioPlaying) {
        audio.pause();
        setAudioPlaying(false);
      } else {
        await audio.play();
        setAudioPlaying(true);
      }
    } catch (err) {
      setAudioPlaying(false);
    }
  };

  if (!hasPreview) return null;

  return (
    <>
      <Tooltip title="Abrir pre-visualizacao">
        <Button
          variant="contained"
          size="small"
          onClick={handleOpen}
          startIcon={<VisibilityOutlinedIcon />}
          sx={PREVIEW_BUTTON_SX}
        >
          Preview
        </Button>
      </Tooltip>

      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        onClose={handleClose}
        onClick={stopPropagation}
        PaperProps={{
          sx: {
            borderRadius: "14px",
            height: { xs: "85vh", md: "78vh" },
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogContent sx={{ flex: 1, p: 0, m: 0, display: "flex", minHeight: 0 }}>
          {isVideo && (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                backgroundColor: "#0f172a",
                overflow: "hidden",
                m: 0,
                position: "relative",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1,
                  py: 0.8,
                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.25) 70%, rgba(0,0,0,0) 100%)",
                }}
              >
                <Typography sx={{ color: "#fff", fontSize: 12, fontWeight: 700 }}>
                  {VIDEO_PREVIEW_MESSAGE}
                </Typography>
                <IconButton
                  onClick={handleClose}
                  size="small"
                  sx={{
                    color: "#fff",
                    backgroundColor: "rgba(0,0,0,0.35)",
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.55)" },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <video
                ref={videoRef}
                controls
                preload="metadata"
                style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                src={record.url}
                controlsList="nodownload noplaybackrate noremoteplayback"
                // onTimeUpdate={(event) => {
                //   const element = event.currentTarget;
                //   if (!element.duration) return;
                //   const maxTime = element.duration * 0.3;
                //   if (element.currentTime >= maxTime) {
                //     element.pause();
                //     element.currentTime = 0;
                //   }
                // }}
              />
            </Box>
          )}

          {isAudio && (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                backgroundColor: "#0f172a",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  zIndex: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1,
                  py: 0.8,
                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.25) 70%, rgba(0,0,0,0) 100%)",
                }}
              >
                <Typography sx={{ color: "#fff", fontSize: 12, fontWeight: 700 }}>
                  {AUDIO_PREVIEW_MESSAGE}
                </Typography>
                <IconButton
                  onClick={handleClose}
                  size="small"
                  sx={{
                    color: "#fff",
                    backgroundColor: "rgba(0,0,0,0.35)",
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.55)" },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  height: "100%",
                  minHeight: 0,
                  position: "relative",
                  overflow: "hidden",
                  p: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {record.thumbnail_url && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      backgroundImage: `url(${record.thumbnail_url})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      transform: "scale(1.08)",
                      filter: "blur(16px)",
                      opacity: 0.45,
                      zIndex: 0,
                    }}
                  />
                )}
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(135deg, rgba(15,23,42,0.55) 0%, rgba(15,23,42,0.72) 100%)",
                    zIndex: 0,
                  }}
                />
                <Box
                  sx={{
                    width: { xs: "92%", sm: "86%", md: "78%" },
                    maxWidth: 900,
                    borderRadius: "16px",
                    overflow: "hidden",
                    border: "1px solid rgba(148,163,184,0.2)",
                    background: "rgba(15,23,42,0.35)",
                    backdropFilter: "blur(3px)",
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: { xs: 1.5, md: 2.5 },
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: 96, md: 180 },
                      height: { xs: 96, md: 180 },
                      borderRadius: "12px",
                      overflow: "hidden",
                      backgroundColor: "#0f172a",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {record.thumbnail_url ? (
                      <img
                        src={record.thumbnail_url}
                        alt={record.name || "thumbnail"}
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                    ) : (
                      <Typography sx={{ color: "#cbd5e1", fontSize: 12, fontWeight: 700 }}>SEM CAPA</Typography>
                    )}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, color: "#f8fafc", mb: 1, position: "relative", zIndex: 1 }}>
                      {record.name || "Audio"}
                    </Typography>
                    <Box
                      sx={{
                        borderRadius: "12px",
                        border: "1px solid rgba(148,163,184,0.25)",
                        p: 1.25,
                        background: "linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(241,245,249,0.88) 100%)",
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <Button
                          onClick={toggleAudioPlay}
                          variant="contained"
                          size="small"
                          sx={{
                            minWidth: 0,
                            width: 36,
                            height: 36,
                            borderRadius: "10px",
                            backgroundColor: audioPlaying ? "#dc2626" : "#0f766e",
                            "&:hover": {
                              backgroundColor: audioPlaying ? "#b91c1c" : "#0d5f59",
                            },
                          }}
                        >
                          {audioPlaying ? (
                            <PauseCircleOutlineIcon fontSize="small" />
                          ) : (
                            <PlayCircleOutlineIcon fontSize="small" />
                          )}
                        </Button>
                        <Chip
                          size="small"
                          label={`${formatTime(audioCurrentTime)} / ${formatTime(audioDuration)}`}
                          sx={{
                            fontWeight: 700,
                            fontSize: 11,
                            color: "#334155",
                            backgroundColor: "#e2e8f0",
                          }}
                        />
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={audioDuration > 0 ? Math.min(100, (audioCurrentTime / audioDuration) * 100) : 0}
                        sx={{
                          height: 6,
                          borderRadius: 999,
                          backgroundColor: "rgba(15,23,42,0.1)",
                          "& .MuiLinearProgress-bar": { borderRadius: 999, backgroundColor: "#0f766e" },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {isText && (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                backgroundColor: "#f8fafc",
                p: 2,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1.5,
                  gap: 1,
                }}
              >
                <Typography sx={{ color: "#334155", fontSize: 12, fontWeight: 700 }}>
                  {TEXT_PREVIEW_MESSAGE}
                </Typography>
                <IconButton
                  onClick={handleClose}
                  size="small"
                  sx={{
                    color: "#334155",
                    backgroundColor: "rgba(148,163,184,0.2)",
                    "&:hover": { backgroundColor: "rgba(148,163,184,0.35)" },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  borderRadius: "14px",
                  border: "1px solid rgba(15,23,42,0.1)",
                  background: "#ffffff",
                  p: 2,
                  overflow: "auto",
                }}
              >
                <Typography sx={{ fontWeight: 700, color: "#0f172a", mb: 1 }}>{record.name || "Texto"}</Typography>
                <Typography component="div" sx={{ color: "#1e293b", lineHeight: 1.6 }}>
                  <span dangerouslySetInnerHTML={{ __html: textBody }} />
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export { CustomerMediaPreview };
